import React, { useState, useEffect, useMemo } from 'react';
import { ChevronRight, ChevronDown, Users, ShieldCheck, ShieldAlert, User as UserIcon, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_TONE = {
  admin: { bg: 'bg-purple-50', border: 'border-purple-200', icon: ShieldCheck, color: 'text-purple-700' },
  gate: { bg: 'bg-amber-50', border: 'border-amber-200', icon: ShieldAlert, color: 'text-amber-700' },
  staff: { bg: 'bg-blue-50', border: 'border-blue-200', icon: UserIcon, color: 'text-blue-700' }
};

// Recursive node filter — keep node if itself or any descendant matches.
function filterTree(node, q) {
  const kids = (node.children || []).map(c => filterTree(c, q)).filter(Boolean);
  const hit =
    (node.name || '').toLowerCase().includes(q) ||
    (node.mobile || '').toLowerCase().includes(q) ||
    (node.staff_id || '').toLowerCase().includes(q);
  if (!hit && kids.length === 0) return null;
  return { ...node, children: kids, _matched: hit };
}

// Flatten the tree into ordered rows the renderer can iterate over.
// Each row = { node, depth, hasChildren, expanded, hidden }
function flattenTree(roots, collapsed) {
  const rows = [];
  const walk = (node, depth, parentHidden) => {
    const isCollapsed = collapsed.has(node.id);
    const hidden = parentHidden;
    rows.push({
      id: node.id,
      node,
      depth,
      hasChildren: (node.children || []).length > 0,
      expanded: !isCollapsed,
      hidden
    });
    if (node.children && node.children.length > 0) {
      const childrenHidden = parentHidden || isCollapsed;
      for (const c of node.children) walk(c, depth + 1, childrenHidden);
    }
  };
  for (const r of roots) walk(r, 0, false);
  return rows;
}

export default function StaffHierarchyTree() {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState('');
  // Set of collapsed node ids. Default: empty (everything expanded).
  const [collapsed, setCollapsed] = useState(() => new Set());

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API_URL}/api/admin/staff/hierarchy`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!r.ok) throw new Error(`Status ${r.status}`);
        const d = await r.json();
        setTree(d);
      } catch (e) {
        setErr(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filteredRoots = useMemo(() => {
    if (!tree) return [];
    const q = search.trim().toLowerCase();
    if (!q) return tree.roots;
    return tree.roots.map(r => filterTree(r, q)).filter(Boolean);
  }, [tree, search]);

  const rows = useMemo(() => flattenTree(filteredRoots, collapsed), [filteredRoots, collapsed]);

  const toggleNode = (id) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allIds = useMemo(() => {
    const ids = [];
    const walk = (n) => { ids.push(n.id); (n.children || []).forEach(walk); };
    filteredRoots.forEach(walk);
    return ids;
  }, [filteredRoots]);

  const allCollapsed = collapsed.size > 0 && allIds.every(id => collapsed.has(id));

  if (loading) return <div className="text-center py-8 text-gray-500">Loading hierarchy…</div>;
  if (err) return <div className="bg-red-50 border border-red-200 text-red-700 rounded p-4 text-sm">Error: {err}</div>;
  if (!tree) return null;

  const noTeams = tree.roots.length > 0 && tree.roots.every(r => !r.children || r.children.length === 0);

  return (
    <div data-testid="staff-hierarchy-tree">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-semibold text-lg text-gray-900">Org Chart</h2>
          <p className="text-xs text-gray-500">
            Built from primary reporting (Report 1). {tree.total} people · {tree.roots.length} root{tree.roots.length === 1 ? '' : 's'}
            {tree.orphans ? ` · ${tree.orphans} orphan${tree.orphans === 1 ? '' : 's'}` : ''}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, mobile, staff ID…"
              className="pl-8 pr-3 py-1.5 border rounded-lg text-sm w-64"
              data-testid="hierarchy-search"
            />
          </div>
          <button
            onClick={() => {
              if (allCollapsed) setCollapsed(new Set());
              else setCollapsed(new Set(allIds));
            }}
            className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium"
            data-testid="hierarchy-toggle-all"
          >{allCollapsed ? 'Expand all' : 'Collapse all'}</button>
        </div>
      </div>

      {noTeams && !search && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-900">
          <strong>Heads up:</strong> Most users have no <em>Report 1</em> set, so the tree is flat.
          Open the Table view and pick a Report 1 for each staff to build the org chart.
        </div>
      )}

      <div className="space-y-1">
        {rows.length === 0 && (
          <div className="text-center py-6 text-gray-500 text-sm">No matching people</div>
        )}
        {rows.filter(r => !r.hidden).map(r => {
          const node = r.node;
          const tone = ROLE_TONE[node.role] || ROLE_TONE.staff;
          const Icon = tone.icon;
          return (
            <div
              key={r.id}
              className={`flex items-center gap-2 ${tone.bg} ${tone.border} ${node._matched ? 'ring-2 ring-yellow-400' : ''} border rounded-lg px-3 py-2 hover:shadow-sm transition`}
              style={{ marginLeft: r.depth * 24 }}
              data-testid={`hierarchy-node-${r.id}`}
            >
              {r.hasChildren ? (
                <button
                  onClick={() => toggleNode(r.id)}
                  className="flex items-center justify-center w-5 h-5 hover:bg-white rounded"
                  data-testid={`hierarchy-expand-${r.id}`}
                >
                  {r.expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
              ) : (
                <span className="w-5 h-5" />
              )}
              <Icon size={16} className={tone.color} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-sm text-gray-900 truncate">{node.name}</span>
                  <span className={`text-[10px] uppercase tracking-wide font-semibold ${tone.color}`}>{node.role}</span>
                  {node.staff_id && <span className="text-[11px] text-gray-500">#{node.staff_id}</span>}
                </div>
                <div className="text-[11px] text-gray-500 truncate">
                  {node.mobile}
                  {node.geo_fence_location_id && <span className="ml-1">· {node.geo_fence_location_id}</span>}
                </div>
              </div>
              {node.down_team_count > 0 && (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-white/80 rounded-full text-[11px] font-semibold text-gray-700 border border-gray-200">
                  <Users size={11} />
                  {node.down_team_count} below
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
